import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Button, Table } from "react-bootstrap";
import axios from "axios";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../../layouts/PageLayout";

export default function CheckerIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkers, setCheckers] = useState([]);
    const [search, setSearch] = useState("");

    const fetchCheckers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/checker`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            if (response.data?.success) {
                setCheckers(response.data.data || []);
            } else {
                setError(response.data?.message || "Failed to fetch checkers.");
            }
        } catch (err) {
            console.error("Error fetching checkers:", err);
            setError(err.response?.data?.message || "Failed to fetch checkers list.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCheckers();
    }, [fetchCheckers]);

    const filteredCheckers = checkers.filter((c) => {
        const q = search.toLowerCase();
        return (
            c.name?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q) ||
            c.nickname?.toLowerCase().includes(q)
        );
    });

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Checkers")} ({filteredCheckers.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Checkers")}</li>
                                </ul>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => navigate("/checkers/create")}
                            >
                                <i className="material-icons me-1 vertical-middle">add</i>
                                {t("Add Checker")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-4">
                            <Col md={6} xl={4}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder="Search by Name, Email or Nickname..."
                                    label="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading checkers...")}</p>
                            </div>
                        ) : (
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head primary">
                                        <tr>
                                            <th>Name</th>
                                            <th>Nickname</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body even">
                                        {filteredCheckers.length > 0 ? (
                                            filteredCheckers.map((checker) => (
                                                <tr key={checker.id}>
                                                    <td className="fw-semibold">{checker.name || "N/A"}</td>
                                                    <td>{checker.nickname || "N/A"}</td>
                                                    <td>
                                                        <span
                                                            className={`badge ${checker.status ? "bg-success" : "bg-secondary"
                                                                }`}
                                                        >
                                                            {checker.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="mc-table-action d-flex align-items-center gap-2">
                                                            {/* View Details Button */}
                                                            <button
                                                                type="button"
                                                                className="material-icons view border-0 bg-transparent text-info"
                                                                onClick={() => navigate(`/checkers/show/${checker.id}`)}
                                                                title="View Details"
                                                            >
                                                                visibility
                                                            </button>

                                                            {/* Edit Button */}
                                                            <button
                                                                type="button"
                                                                className="material-icons edit border-0 bg-transparent text-primary"
                                                                onClick={() => navigate(`/checkers/edit/${checker.id}`, { state: { checker } })}
                                                                title="Edit"
                                                            >
                                                                edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="text-center py-4">
                                                    {t("No checkers found")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}